import clsx from 'clsx'
import { pipe } from 'fp-ts/function'
import { getOrElse } from 'fp-ts/Option'
import { type ReactElement } from 'react'

import { useCommonServices } from '@lib/hooks'

import { Column } from '@components/ui-core/column/column'
import { Row } from '@components/ui-core/row/row'
import { Text } from '@components/ui-core/text/text'

import { CtaButton } from '../cta-button/cta-button'
import { ProductIcons } from '../product-icons/product-icons'
import { type Plan as PlanType, type PlanKind, type ButtonStyleVariant } from '../types'

import { monthlyPrices } from './utils'

import styles from './plan.module.scss'

export function Plan({
  ctaDisabled,
  plan,
  highlighted,
}: {
  readonly plan: PlanType<PlanKind>
  readonly ctaDisabled: boolean
  readonly highlighted: PlanKind
}): ReactElement {
  const {
    kind,
    targetAudience,
    title,
    description,
    billing: { price, cadence, monthlyPrice },
    cta: { label, url, disabledLabel },
    features: { products, included, list },
  } = plan

  const { shWebServerService } = useCommonServices()

  const plansData = shWebServerService.get().extras?.plansData

  const { whenBilledAnnually, whenBilledMonthly } = pipe(
    monthlyPrices(kind, plansData),
    getOrElse(() => ({
      whenBilledAnnually: price,
      whenBilledMonthly: monthlyPrice?.price,
    })),
  )

  const planHighlighted = kind === highlighted
  const free = kind === 'free'
  const category: ButtonStyleVariant = planHighlighted ? 'primary' : 'secondary'
  const finalLabel = ctaDisabled ? disabledLabel : label

  return (
    <Column
      mainAxisAlignment="start"
      crossAxisAlignment="center"
      className={clsx(
        styles.planContainer,
        planHighlighted && styles.highlighted,
        free && styles.free,
      )}
    >
      <Row
        mainAxisAlignment="center"
        crossAxisAlignment="center"
        className={clsx(styles.highlightBox, planHighlighted && styles.highlighted)}
      >
        {planHighlighted && 'Best value'}
      </Row>
      <Column
        gap="6x"
        crossAxisAlignment="start"
        mainAxisAlignment="start"
        className={clsx(styles.plan, planHighlighted && styles.highlighted)}
      >
        <Column gap="4x" mainAxisAlignment="start" className={styles.top}>
          <section className={styles.targetAudience}>
            <Text as="span" variant="bodySmall" fontWeight="semibold">
              {targetAudience}
            </Text>
          </section>
          <Text as="h2" variant="headerLarge" fontWeight="semibold" className={styles.title}>
            {title}
          </Text>
          <Text as="p" variant="bodyLarge" className={styles.description}>
            {description}
          </Text>
        </Column>

        <Column gap="1x" mainAxisAlignment="start" className={styles.prices}>
          <Text as="h3" variant="headerLarge" fontWeight="semibold" className={styles.price}>
            {whenBilledAnnually}
          </Text>
          <section className={styles.cadence}>{cadence}</section>
          <section className={styles.monthlyPrice}>
            {whenBilledMonthly && (
              <span>
                <strong>{whenBilledMonthly}</strong> <span>{monthlyPrice?.label}</span>
              </span>
            )}
          </section>
        </Column>

        <CtaButton
          placement="Section"
          category={category}
          disabled={ctaDisabled}
          className={styles.cta}
          url={url}
          label={finalLabel}
        />

        <Column gap="4x">
          <Column
            gap="2x"
            mainAxisAlignment="start"
            crossAxisAlignment="start"
            className={styles.productIcons}
          >
            <ProductIcons availability={products} />
          </Column>
          <span className={styles.included}>{included}</span>
          <Column gap="3x" as="ul" className={styles.list}>
            {list.map(feature => (
              <li key={feature}>
                <span>{feature}</span>
              </li>
            ))}
          </Column>
        </Column>
      </Column>
    </Column>
  )
}
